import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { Trash2, ShoppingBag, ArrowRight, Lock } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CartPage() {
  const { items, removeItem, clearCart, total } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/checkout`, {
        origin_url: window.location.origin,
        affiliate_code: localStorage.getItem('ref_code') || null
      }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 flex flex-col items-center justify-center" data-testid="empty-cart">
        <ShoppingBag size={48} className="text-white/10 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Your Cart is Empty</h1>
        <p className="text-sm text-white/40 mb-6 text-center max-w-sm">Looks like you haven't added anything yet. Browse Hettie's courses or trading robots to get started.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/all-courses" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-400 text-black text-sm font-semibold hover:bg-amber-300 transition-colors" data-testid="browse-courses-btn">
            Browse Courses <ArrowRight size={16} />
          </Link>
          <Link to="/products" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/10 text-white/70 text-sm font-semibold hover:border-white/20 transition-colors" data-testid="browse-products-btn">
            Browse Robots
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-36 sm:pb-20 px-4 sm:px-6 lg:px-8" data-testid="cart-page">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold text-white mb-8">Your Cart</h1>
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.slug} className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid={`cart-item-${item.slug}`}>
              {item.image_url && (
                <img src={item.image_url} alt={item.name} className="w-16 h-12 rounded-lg object-cover" />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-white/90 truncate">{item.name}</h3>
                <span className="text-[10px] text-white/30">{item.platform}</span>
              </div>
              <span className="text-lg font-bold text-white">${item.price}</span>
              <button
                onClick={() => removeItem(item.slug)}
                className="p-2 text-white/30 hover:text-red-400 transition-colors"
                data-testid={`remove-${item.slug}`}
                aria-label={`Remove ${item.name} from cart`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Desktop / tablet checkout card */}
        <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 hidden sm:block">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-white/50">Total</span>
            <span className="text-2xl font-bold text-white" data-testid="cart-total">${total.toFixed(2)} USD</span>
          </div>
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="checkout-btn"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {!user && <Lock size={14} />}
                {user ? 'Proceed to Checkout' : 'Sign In to Checkout'}
              </>
            )}
          </button>
          <p className="mt-3 text-center text-[10px] text-white/20">Secure payment via Stripe</p>
        </div>

        <button
          onClick={clearCart}
          className="mt-4 text-xs text-white/30 hover:text-white/50 transition-colors"
          data-testid="clear-cart-btn"
        >
          Clear Cart
        </button>
      </div>

      {/* Mobile-only sticky checkout bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#050814]/95 backdrop-blur-md border-t border-white/10 px-4 py-3 shadow-2xl" data-testid="mobile-checkout-bar">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-white/50">Total</span>
          <span className="text-lg font-bold text-white" data-testid="cart-total-mobile">${total.toFixed(2)} USD</span>
        </div>
        <button
          onClick={handleCheckout}
          disabled={loading}
          className="w-full py-3 rounded-lg bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="checkout-btn-mobile"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              {!user && <Lock size={14} />}
              {user ? 'Checkout' : 'Sign In to Checkout'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
